import {
  cleaningMetadataSchema,
  cleaningPayloadSchema,
  DispenserTimelineItem,
  dispensingMetadataSchema,
  dispensingPayloadSchema,
  errorMetadataSchema,
  errorPayloadSchema,
  flushPayloadSchema,
  purgeMetadataSchema,
  purgePayloadSchema,
  recirculationMetadataSchema,
  recirculationPayloadSchema,
  refillMetadataSchema,
  refillPayloadSchema,
  stirringMetadataSchema,
  stirringPayloadSchema,
} from '@/features/dispensers/services/schemas.ts';
import { EventTimelineItemProps } from '@/features/events/components/events-timeline/subcomponents/event-timeline-item/EventTimelineItem.tsx';
import { EventTimelineGroupItemProps } from '@/features/events/components/events-timeline/subcomponents/event-timeline-group-item/EventTimelineGroupItem.tsx';
import { EventType, EventTypeEnum } from '@/features/events/types/EventType.ts';
import { green, grey, lightBlue, orange, pink, red, teal } from '@mui/material/colors';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { EventMetadata } from '@/features/events/components/events-timeline/subcomponents/event-metadata/EventMetadata.tsx';
import { plural, t } from '@lingui/core/macro';
import QuestionMarkOutlinedIcon from '@mui/icons-material/QuestionMarkOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import { DispensedColor } from '@/features/events/components/events-timeline/subcomponents/dispensed-color/DispensedColor.tsx';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ColorizeRoundedIcon from '@mui/icons-material/ColorizeRounded';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import BrushRoundedIcon from '@mui/icons-material/BrushRounded';
import { IncompleteDataLabel } from '@/features/events/components/events-timeline/subcomponents/incomplete-data-label/IncompleteDataLabel.tsx';
import { formatNumber } from '@/common/utils/formatNumber.ts';
import { FormattedEventResult } from '@/features/events/components/events-timeline/subcomponents/formatted-event-result/FormattedEventResult.tsx';
import { ReactNode } from 'react';
import { z } from 'zod';

type EventTimelineItemPropsResult = Pick<
  EventTimelineItemProps,
  'importantEvent' | 'labels' | 'metadata'
> &
  Pick<EventTimelineGroupItemProps, 'iconProps'> &
  Partial<Pick<EventTimelineGroupItemProps, 'countLabel'>>;

type EventTimelineItemPropsMapper = (item: DispenserTimelineItem) => EventTimelineItemPropsResult;

interface ResultField {
  key: ReactNode;
  value: ReactNode;
}

function createResultField(result: number | null): ResultField {
  return {
    key: t`Result`,
    value: typeof result === 'number' ? <FormattedEventResult result={result} /> : null,
  };
}

function hasCompleteData(item: DispenserTimelineItem, metadataSchema: z.ZodTypeAny): boolean {
  return item.eventsCount === 1 ? metadataSchema.safeParse(item.payload).success : true;
}

function createIncompleteDataLabel(isComplete: boolean): ReactNode {
  return !isComplete && <IncompleteDataLabel />;
}

// ============================================================================
// Event Mappers - Events with Payload
// ============================================================================

const mapRefill: EventTimelineItemPropsMapper = (item) => {
  const payload = refillPayloadSchema.nullable().parse(item.payload);
  const isComplete = hasCompleteData(item, refillMetadataSchema);

  return {
    iconProps: {
      color: lightBlue,
      children: <Inventory2OutlinedIcon />,
    },
    metadata: (
      <EventMetadata
        data={[
          { key: t`Canister`, value: payload?.canister },
          { key: t`Colorant`, value: payload?.colorant },
          { key: t`Amount`, value: formatNumber(payload?.amount, 'milliliters') },
          { key: t`Final level`, value: formatNumber(payload?.finalLevel, 'milliliters') },
        ]}
      />
    ),
    labels: createIncompleteDataLabel(isComplete),
  };
};

const mapDispensing: EventTimelineItemPropsMapper = (item) => {
  const payload = dispensingPayloadSchema.nullable().parse(item.payload);
  const isComplete = hasCompleteData(item, dispensingMetadataSchema);
  const resultField = createResultField(item.result);

  return {
    iconProps: {
      color: green,
      children: <WaterDropOutlinedIcon />,
    },
    metadata: (
      <EventMetadata
        data={[
          { key: t`Product`, value: payload?.product },
          { key: t`Package`, value: payload?.package },
          { key: t`Base`, value: payload?.base },
          { key: t`Recipe`, value: payload?.recipe },
          { key: t`Fan`, value: payload?.fan },
          {
            key: t`Colorants`,
            value: payload?.colorants?.map((colorant) => colorant.name ?? t`Unknown`).join(', '),
          },
          resultField,
        ]}
      />
    ),
    importantEvent: true,
    labels: (
      <>
        <DispensedColor color={payload?.colorRgb} />
        {createIncompleteDataLabel(isComplete)}
      </>
    ),
  };
};

const mapStirring: EventTimelineItemPropsMapper = (item) => {
  const payload = stirringPayloadSchema.nullable().parse(item.payload);
  const isComplete = hasCompleteData(item, stirringMetadataSchema);
  const resultField = createResultField(item.result);

  return {
    iconProps: {
      color: teal,
      children: <RotateRightIcon />,
    },
    metadata: (
      <EventMetadata
        data={[
          { key: t`Canister`, value: payload?.canister },
          { key: t`Colorant`, value: payload?.colorant },
          { key: t`Duration`, value: formatNumber(payload?.duration, 'seconds') },
          { key: t`Intensity`, value: payload?.intensity },
          resultField,
        ]}
      />
    ),
    importantEvent: true,
    countLabel: plural(item.eventsCount, {
      other: '# canisters',
      one: '# canister',
    }),
    labels: createIncompleteDataLabel(isComplete),
  };
};

const mapError: EventTimelineItemPropsMapper = (item) => {
  const payload = errorPayloadSchema.nullable().parse(item.payload);
  const isComplete = hasCompleteData(item, errorMetadataSchema);
  const resultField = createResultField(item.result);

  return {
    iconProps: {
      color: red,
      children: <WarningAmberRoundedIcon />,
    },
    importantEvent: true,
    metadata: (
      <EventMetadata
        data={[
          { key: t`Channel`, value: payload?.channel },
          { key: t`Canister`, value: payload?.canister },
          resultField,
        ]}
      />
    ),
    labels: createIncompleteDataLabel(isComplete),
  };
};

const mapPurgeOrFlush: EventTimelineItemPropsMapper = (item) => {
  const payload =
    item.eventType === EventTypeEnum.PURGE
      ? purgePayloadSchema.nullable().parse(item.payload)
      : flushPayloadSchema.nullable().parse(item.payload);
  const isComplete = hasCompleteData(item, purgeMetadataSchema);
  const resultField = createResultField(item.result);

  return {
    iconProps: {
      color: orange,
      children: <ColorizeRoundedIcon />,
    },
    importantEvent: true,
    metadata: payload ? (
      <EventMetadata
        data={[
          { key: t`Colorant`, value: payload.colorant },
          { key: t`Amount`, value: formatNumber(payload.amount, 'milliliters') },
          resultField,
        ]}
      />
    ) : null,
    countLabel: plural(item.eventsCount, {
      other: '# colorants',
      one: '# colorant',
    }),
    labels: createIncompleteDataLabel(isComplete),
  };
};

const mapCleaning: EventTimelineItemPropsMapper = (item) => {
  const payload = cleaningPayloadSchema.nullable().parse(item.payload);
  const isComplete = hasCompleteData(item, cleaningMetadataSchema);
  const resultField = createResultField(item.result);

  return {
    iconProps: {
      color: lightBlue,
      children: <CleaningServicesRoundedIcon />,
    },
    metadata: (
      <EventMetadata
        data={[
          { key: t`Colorant`, value: payload?.colorant },
          { key: t`Duration`, value: formatNumber(payload?.duration, 'seconds') },
          { key: t`Channel`, value: payload?.channel },
          resultField,
        ]}
      />
    ),
    labels: createIncompleteDataLabel(isComplete),
    countLabel: plural(item.eventsCount, {
      other: '# channels',
      one: '# channel',
    }),
  };
};

const mapRecirculation: EventTimelineItemPropsMapper = (item) => {
  const payload = recirculationPayloadSchema.nullable().parse(item.payload);
  const isComplete = hasCompleteData(item, recirculationMetadataSchema);
  const resultField = createResultField(item.result);

  return {
    iconProps: {
      color: pink,
      children: <CachedRoundedIcon />,
    },
    metadata: (
      <EventMetadata
        data={[
          { key: t`Colorant`, value: payload?.colorant },
          { key: t`Channel`, value: payload?.channel },
          resultField,
        ]}
      />
    ),
    labels: createIncompleteDataLabel(isComplete),
    countLabel: plural(item.eventsCount, {
      other: '# channels',
      one: '# channel',
    }),
  };
};

// ============================================================================
// Event Mappers - Events without Payload (icon-only)
// ============================================================================

const mapSwStart: EventTimelineItemPropsMapper = () => ({
  iconProps: {
    color: lightBlue,
    children: <PlayArrowRoundedIcon />,
  },
});

const mapSwShutdown: EventTimelineItemPropsMapper = () => ({
  iconProps: {
    color: lightBlue,
    children: <StopRoundedIcon />,
  },
});

const mapStart: EventTimelineItemPropsMapper = () => ({
  iconProps: {
    color: lightBlue,
    children: <PowerSettingsNewRoundedIcon />,
  },
});

const mapShutdown: EventTimelineItemPropsMapper = () => ({
  iconProps: {
    color: lightBlue,
    children: <PowerSettingsNewRoundedIcon />,
  },
});

const mapBrush: EventTimelineItemPropsMapper = () => ({
  iconProps: {
    color: lightBlue,
    children: <BrushRoundedIcon />,
  },
});

const mapUnknown: EventTimelineItemPropsMapper = () => ({
  iconProps: {
    color: grey,
    children: <QuestionMarkOutlinedIcon />,
  },
});

// ============================================================================
// Event Mapper Registry
// ============================================================================

const eventMapperRegistry: Partial<Record<EventType, EventTimelineItemPropsMapper>> = {
  [EventTypeEnum.REFILL]: mapRefill,
  [EventTypeEnum.DISPENSING]: mapDispensing,
  [EventTypeEnum.STIRRING]: mapStirring,
  [EventTypeEnum.ERROR]: mapError,
  [EventTypeEnum.PURGE]: mapPurgeOrFlush,
  [EventTypeEnum.FLUSH]: mapPurgeOrFlush,
  [EventTypeEnum.CLEANING]: mapCleaning,
  [EventTypeEnum.RECIRCULATION]: mapRecirculation,
  [EventTypeEnum.SW_START]: mapSwStart,
  [EventTypeEnum.SW_SHUTDOWN]: mapSwShutdown,
  [EventTypeEnum.START]: mapStart,
  [EventTypeEnum.SHUTDOWN]: mapShutdown,
  [EventTypeEnum.BRUSH]: mapBrush,
};

// ============================================================================
// Main Export
// ============================================================================

export function getEventTimelineItemProps(
  item: DispenserTimelineItem
): EventTimelineItemPropsResult {
  const mapper = eventMapperRegistry[item.eventType] ?? mapUnknown;
  return mapper(item);
}
